import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Chip, FormControlLabel, Grid, LinearProgress, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PaymentsIcon from '@mui/icons-material/Payments';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import StatTile from '../../../common/stat_tile';
import useNotification from '../../../../core/hooks/use_notification';
import { getWarehouseStockSummariesAction, getWarehouseStockSummariesState } from '../../../../features/inventory/get_warehouse_stock_summaries';
import { getWarehouseStockSummariesNotificationData } from '../../../../core/utils/notification_utils';
import { GET_ROUTE_WAREHOUSE_STOCK } from '../../../../core/constants';
import { WarehouseTypeEnum } from '../../../../core/enums/inventory/warehouse';
import FilterPaper from '../../../common/filter_paper';
import { SelectFieldMultiple, SelectFieldMultipleWithMore } from '../../../common/select_field';
import { getWarehouseName } from '../../../common/utils/warehouse_utils';
import { ExpirationChip, formatCurrency, formatNumber, getStockLocationName, getWarehouseTypeLabel } from './stock_utils';

const warehouseTypeOptions = [
    { value: WarehouseTypeEnum.MAIN.value, label: 'Magazyn główny' },
    { value: WarehouseTypeEnum.INVENTORY.value, label: 'Magazyn inwentaryzacyjny' },
    { value: WarehouseTypeEnum.POD.value, label: 'Punkty sprzedaży' },
    { value: WarehouseTypeEnum.POP.value, label: 'Punkty odbioru' },
    { value: WarehouseTypeEnum.UNKNOWN.value, label: 'Inne' }
];

const isPodWithoutDevice = (warehouse) => warehouse?.type === WarehouseTypeEnum.POD.value && warehouse?.pod?.serviceId == null;


const WarehouseStocksPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showErrorMessage } = useNotification();
    const getWarehouseStockSummaries = useSelector(getWarehouseStockSummariesState);

    const [types, setTypes] = useState([]);
    const [warehouseIds, setWarehouseIds] = useState([]);
    const [showEmpty, setShowEmpty] = useState(false);

    useEffect(() => {
        dispatch(getWarehouseStockSummariesAction())
            .then(response => showErrorMessage(getWarehouseStockSummariesNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    const summaries = useMemo(() => getWarehouseStockSummaries?.data || [], [getWarehouseStockSummaries?.data]);

    const rows = useMemo(() => summaries
        .filter(summary => types.length === 0 || types.includes(summary?.warehouse?.type || WarehouseTypeEnum.UNKNOWN.value))
        .filter(summary => warehouseIds.length === 0 || warehouseIds.includes(summary?.warehouseId))
        .filter(summary => showEmpty || summary?.quantity > 0),
    [summaries, types, warehouseIds, showEmpty]);

    const warehouses = summaries.filter(summary => summary?.warehouse).map(summary => summary.warehouse);

    const totals = useMemo(() => ({
        quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
        value: rows.reduce((sum, row) => sum + Number(row.purchaseNetValue || 0), 0),
        expired: rows.reduce((sum, row) => sum + row.expiredQuantity, 0)
    }), [rows]);

    return (
        <Box mb={ 6 }>
            <Stack direction="column" spacing={ 0.5 } mb={ 3 }>
                <Typography variant="h1" sx={ { m: 0 } }>
                    Stany magazynowe
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={ { fontWeight: 500 } }>
                    Aktualne stany we wszystkich magazynach sklepu
                </Typography>
            </Stack>

            <Stack spacing={ 3 }>
                <FilterPaper title="Filtry stanów magazynowych" sx={ { mb: 0 } }>
                    <Grid container spacing={ 3 } alignItems="center">
                        <Grid item xs={ 12 } sm={ 6 }>
                            <SelectFieldMultiple
                                size="small"
                                label="Rodzaj magazynu (domyślnie wszystkie)"
                                options={ warehouseTypeOptions }
                                value={ types }
                                onChange={ event => setTypes(event.target.value) }
                            />
                        </Grid>
                        <Grid item xs={ 12 } sm={ 6 }>
                            <FormControlLabel
                                control={ <Switch checked={ showEmpty } onChange={ event => setShowEmpty(event.target.checked) }/> }
                                label="Pokaż puste magazyny"
                            />
                        </Grid>
                        <Grid item xs={ 12 }>
                            <SelectFieldMultipleWithMore
                                size="small"
                                label="Pokaż dla magazynów (domyślnie wszystkie)"
                                defaultOptions={ warehouses.filter(warehouse => !isPodWithoutDevice(warehouse)).map(warehouse => ({ value: warehouse.internalId, label: getWarehouseName(warehouse) })) }
                                extraOptions={ warehouses.filter(isPodWithoutDevice).map(warehouse => ({ value: warehouse.internalId, label: getWarehouseName(warehouse) })) }
                                value={ warehouseIds }
                                onChange={ event => setWarehouseIds(event.target.value) }
                            />
                        </Grid>
                    </Grid>
                </FilterPaper>

                <Box>
                    <Grid container spacing={ 2 }>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <StatTile label="Sztuk na stanie" value={ formatNumber(totals.quantity) } icon={ <Inventory2Icon/> } color="primary"/>
                        </Grid>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <StatTile label="Wartość netto zakupu" value={ formatCurrency(totals.value) } icon={ <PaymentsIcon/> } color="info"/>
                        </Grid>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <StatTile label="Sztuk przeterminowanych" value={ formatNumber(totals.expired) } icon={ <EventBusyIcon/> } color="error" valueColor={ totals.expired > 0 ? 'error.main' : undefined }/>
                        </Grid>
                    </Grid>
                </Box>

                { getWarehouseStockSummaries?.loading && <LinearProgress/> }

                {
                    getWarehouseStockSummaries?.data && (
                        <TableContainer component={ Paper } variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                        <TableCell>Magazyn</TableCell>
                                        <TableCell>Typ</TableCell>
                                        <TableCell align="right">Produkty</TableCell>
                                        <TableCell align="right">Sztuki</TableCell>
                                        <TableCell align="right">Wartość netto zakupu</TableCell>
                                        <TableCell align="right">Przeterminowane</TableCell>
                                        <TableCell align="center">Najbliższy termin</TableCell>
                                        <TableCell padding="checkbox"/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        rows.map(row => {
                                            const clickable = !!row?.warehouse;

                                            return (
                                                <TableRow
                                                    key={ row?.warehouseId }
                                                    hover={ clickable }
                                                    onClick={ () => clickable && navigate(GET_ROUTE_WAREHOUSE_STOCK(row?.warehouseId)) }
                                                    sx={ { cursor: clickable ? 'pointer' : 'default' } }
                                                >
                                                    <TableCell sx={ { fontWeight: 600 } }>
                                                        {
                                                            clickable
                                                                ? getStockLocationName(row?.warehouse, row?.warehouseId)
                                                                : (
                                                                    <Tooltip title="System magazynowy zwrócił stan w lokalizacji, której nie udało się przypisać do magazynu sklepu">
                                                                        <span>{ getStockLocationName(row?.warehouse, row?.warehouseId) }</span>
                                                                    </Tooltip>
                                                                )
                                                        }
                                                    </TableCell>
                                                    <TableCell><Chip size="small" variant="outlined" label={ getWarehouseTypeLabel(row?.warehouse) }/></TableCell>
                                                    <TableCell align="right">{ formatNumber(row?.productCount) }</TableCell>
                                                    <TableCell align="right">{ formatNumber(row?.quantity) }</TableCell>
                                                    <TableCell align="right">{ formatCurrency(row?.purchaseNetValue) }</TableCell>
                                                    <TableCell align="right">
                                                        {
                                                            row?.expiredQuantity > 0
                                                                ? <Chip size="small" color="error" label={ `${ formatNumber(row.expiredQuantity) } szt.` }/>
                                                                : '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center"><ExpirationChip date={ row?.nearestExpirationDate }/></TableCell>
                                                    <TableCell padding="checkbox">{ clickable && <ChevronRightIcon color="action"/> }</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    }
                                    {
                                        rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={ 8 } align="center" sx={ { py: 4, color: 'text.secondary' } }>
                                                    Brak magazynów spełniających kryteria
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                }
            </Stack>
        </Box>
    );
};

export default WarehouseStocksPage;
